import React from 'react';
import {
  BarChart,
  LineChart,
  PieChart,
  Bar,
  Line,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

/**
 * Chart renderer using Recharts library
 * Supports bar, line, and pie charts
 */
export function ChartRenderer({ config }) {
  const { type, data } = config;

  // Default colors for charts
  const COLORS = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#10b981', // green
    '#f59e0b', // yellow
    '#8b5cf6', // purple
    '#ec4899', // pink
  ];

  // Prepare data for Recharts format
  const prepareData = () => {
    if (!data?.labels || !data?.datasets) {
      return [];
    }

    // Transform data from Chart.js format to Recharts format
    return data.labels.map((label, index) => {
      const dataPoint = { name: label };
      data.datasets.forEach((dataset, datasetIndex) => {
        dataPoint[dataset.label || `Dataset ${datasetIndex + 1}`] = dataset.data[index];
      });
      return dataPoint;
    });
  };

  const chartData = prepareData();

  if (!chartData || chartData.length === 0) {
    return (
      <div className="text-gray-500 dark:text-gray-400 text-center p-8">
        No data available for chart
      </div>
    );
  }

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {data.datasets.map((dataset, index) => (
                <Bar
                  key={index}
                  dataKey={dataset.label || `Dataset ${index + 1}`}
                  fill={dataset.backgroundColor || COLORS[index % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {data.datasets.map((dataset, index) => (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={dataset.label || `Dataset ${index + 1}`}
                  stroke={dataset.borderColor || COLORS[index % COLORS.length]}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        // For pie charts, use the first dataset
        const pieData = data.labels.map((label, index) => ({
          name: label,
          value: data.datasets[0]?.data[index] || 0,
        }));

        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <div className="text-red-500 text-center p-8">
            Unsupported chart type: {type}
          </div>
        );
    }
  };

  return (
    <div className="chart-container w-full">
      {renderChart()}
    </div>
  );
}
